/**
 * WelcomeTour — 4-step first-run walkthrough. Spotlights the Context
 * Engine, Annual Plan, Luma and Library. Shows once (localStorage
 * "beacon-tour-done"), replayable from the Help page.
 */
import { useEffect, useState } from "react";

const STEPS = [
  {
    target: '[data-testid="nav-context"]',
    title: "1 · Context Engine",
    body: "Start here. Tell Beacon your site, industry and state(s) — every word it writes is shaped by this. Richer context = sharper packs.",
  },
  {
    target: '[data-testid="nav-plan"]',
    title: "2 · Annual Plan",
    body: "A full year of monthly themes and weekly topics, tuned to the Australian safety calendar. Drag, swap, blank or rebuild any week.",
  },
  {
    target: '[data-testid="luma-launcher"]',
    title: "3 · Luma, your AI offsider",
    body: "Ask Luma to reshape your calendar, build custom weeks, or review any pack for quality and compliance. It always asks before changing anything.",
  },
  {
    target: '[data-testid="nav-library"]',
    title: "4 · Library",
    body: "Every pack you generate lives here — with Luma's Q/C health badges. Re-open, fork onto the calendar, review, or rebuild from there.",
  },
];

export default function WelcomeTour({ onClose }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const step = STEPS[idx];

  useEffect(() => {
    const el = document.querySelector(step.target);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [step.target]);

  const finish = () => {
    window.localStorage.setItem("beacon-tour-done", "true");
    onClose();
  };

  const pad = 8;
  const spot = rect && {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
  // Card below the target for header items; above for the Luma fab.
  const cardBelow = rect ? rect.bottom < window.innerHeight * 0.6 : true;
  const cardStyle = rect
    ? {
        top: cardBelow ? spot.top + spot.height + 14 : undefined,
        bottom: cardBelow
          ? undefined
          : window.innerHeight - spot.top + 14,
        left: Math.max(16, Math.min(spot.left, window.innerWidth - 356)),
      }
    : { top: "38%", left: "50%", transform: "translateX(-50%)" };

  return (
    <div className="beacon-tour" data-testid="welcome-tour">
      {spot ? (
        <div
          className="beacon-tour-spot"
          style={{
            top: spot.top, left: spot.left,
            width: spot.width, height: spot.height,
          }}
        />
      ) : (
        <div className="beacon-tour-scrim" />
      )}
      <div className="beacon-tour-card" style={cardStyle}
        data-testid="tour-card" role="dialog" aria-modal="true">
        <div className="beacon-tour-title" data-testid="tour-step-title">
          {step.title}
        </div>
        <p className="beacon-tour-body">{step.body}</p>
        <div className="beacon-tour-dots" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span key={i}
              className={`beacon-tour-dot${i === idx ? " is-active" : ""}`} />
          ))}
        </div>
        <div className="beacon-tour-actions">
          <button type="button" className="beacon-tour-skip"
            data-testid="tour-skip" onClick={finish}>
            Skip tour
          </button>
          <span className="beacon-tour-nav">
            {idx > 0 && (
              <button type="button" className="beacon-secondary-btn"
                data-testid="tour-back"
                onClick={() => setIdx((i) => i - 1)}>
                ← Back
              </button>
            )}
            <button type="button" className="beacon-cta beacon-cta-compact"
              data-testid="tour-next"
              onClick={() =>
                idx < STEPS.length - 1 ? setIdx((i) => i + 1) : finish()
              }>
              {idx < STEPS.length - 1 ? "Next →" : "Let's go ✦"}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
